import type { Branded } from "@@/utilities/branded";
import type { Except } from "type-fest";
import { z } from "zod";

export type OneMs = 0.001;
export type OneSecond = 1;
export type OneMinute = 60;
export type TwoMinutes = 120;
export type FiveMinutes = 300;
export type TenMinutes = 600;
export type FifteenMinutes = 900;
export type OneHalfHour = 1_800;
export type OneHour = 3_600;
export type TwoHours = 7_200;
export type ThreeHours = 10_800;
export type TwelveHours = 43_200;
export type OneDay = 86_400;
export type OneNight = OneDay;

export const oneMs = 0.001 as DurationInSeconds;
export const oneSecond = 1 as DurationInSeconds;
export const oneMinute = (60 * oneSecond) as DurationInSeconds;
export const twoMinutes = (2 * oneMinute) as DurationInSeconds;
export const fiveMinutes = (5 * oneMinute) as DurationInSeconds;
export const tenMinutes = (10 * oneMinute) as DurationInSeconds;
export const fifteenMinutes = (15 * oneMinute) as DurationInSeconds;
export const oneHalfHour = (2 * fifteenMinutes) as DurationInSeconds;
export const oneHour = (2 * oneHalfHour) as DurationInSeconds;
export const twoHours = (oneHour * 2) as DurationInSeconds;
export const threeHours = (oneHour * 3) as DurationInSeconds;
export const twelveHours = (oneHour * 12) as DurationInSeconds;
export const oneDay = (2 * twelveHours) as DurationInSeconds;
export const oneNight = oneDay;

type TimeSpanInSeconds =
    | OneSecond
    | OneMinute
    | TwoMinutes
    | FiveMinutes
    | TenMinutes
    | FifteenMinutes
    | OneHalfHour
    | OneHour
    | TwoHours
    | ThreeHours
    | TwelveHours
    | OneDay
    // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
    | OneNight;

/**
 * Time in expressed in seconds
 */
type DurationInDays = Branded<number, "DurationInDays">;
type DurationInNights = Branded<number, "DurationInNights">;
type DurationInHours = Branded<number, "DurationInHours">;
type DurationInSeconds = Branded<number, "DurationInSeconds">;
type DurationInMinutes = Branded<number, "DurationInMinutes">;
type DurationInMs = Branded<number, "DurationInMs">;
type Duration =
    | DurationInDays
    | DurationInNights
    | DurationInHours
    | DurationInMinutes
    | DurationInSeconds
    | DurationInMs;

const durationInMsZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInMs>;
const durationInSecondsZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInSeconds>;
const durationInMinutesZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInMinutes>;
const durationInHoursZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInHours>;
const durationInNightsZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInNights>;
const durationInDaysZodSchema =
    z.number() as unknown as z.ZodSchema<DurationInDays>;

type DurationType = {
    nights: DurationInDays | number;
    days: DurationInDays | number;
    hours: DurationInHours | number;
    minutes: DurationInMinutes | number;
    seconds: DurationInSeconds | number;
    ms: DurationInMs | number;
};

const durationTypeInSeconds: {
    [key in keyof DurationType]: DurationInSeconds;
} = {
    nights: oneNight,
    days: oneDay,
    hours: oneHour,
    minutes: oneMinute,
    seconds: oneSecond,
    ms: oneMs,
};

type DurationInputOptions = Partial<{
    noRounding: boolean;
}>;
type DurationInput =
    | ({
          [key in keyof Except<DurationType, "days">]?: never;
      } & {
          days: DurationType["days"];
      })
    | ({
          [key in keyof Except<DurationType, "nights">]?: never;
      } & {
          nights: DurationType["nights"];
      })
    | ({
          [key in keyof Except<DurationType, "hours">]?: never;
      } & {
          hours: DurationType["hours"];
      })
    | ({
          [key in keyof Except<DurationType, "minutes">]?: never;
      } & {
          minutes: DurationType["minutes"];
      })
    | ({
          [key in keyof Except<DurationType, "seconds">]?: never;
      } & {
          seconds: DurationType["seconds"];
      })
    | ({
          [key in keyof Except<DurationType, "ms">]?: never;
      } & {
          ms: DurationType["ms"];
      });

const calculateDuration = <As extends number>(
    input: DurationInput,
    by: DurationInSeconds,
    options?: DurationInputOptions,
): As => {
    const keyWithValue = Object.keys(input).find(
        key => typeof input[key as keyof DurationInput] !== "undefined",
    ) as keyof DurationInput | undefined;
    if (!keyWithValue) throw new Error("input must have a duration set");
    const seconds =
        durationTypeInSeconds[keyWithValue] *
        (input[keyWithValue] as DurationInSeconds);
    if (typeof seconds === "undefined")
        throw new Error("number of seconds cannot be undefined");
    return (
        options?.noRounding ? seconds / by : Math.round(seconds / by)
    ) as As;
};

const inDays = (
    input: DurationInput,
    options?: DurationInputOptions,
): DurationInDays => calculateDuration<DurationInDays>(input, oneDay, options);
const inNights = inDays;
const inHours = (
    input: DurationInput,
    options?: DurationInputOptions,
): DurationInHours =>
    calculateDuration<DurationInHours>(input, oneHour, options);
const inMinutes = (
    input: DurationInput,
    options?: DurationInputOptions,
): DurationInMinutes =>
    calculateDuration<DurationInMinutes>(input, oneMinute, options);
const inSeconds = (
    input: DurationInput,
    options?: DurationInputOptions,
): DurationInSeconds =>
    calculateDuration<DurationInSeconds>(input, oneSecond, options);
const inMilliseconds = (
    input: DurationInput,
    options?: DurationInputOptions,
): DurationInMs => calculateDuration<DurationInMs>(input, oneMs, options);

const duration = {
    inNights,
    inDays,
    inHours,
    inMinutes,
    inSeconds,
    inMilliseconds,
};

export {
    calculateDuration,
    duration,
    durationInDaysZodSchema,
    durationInHoursZodSchema,
    durationInMinutesZodSchema,
    durationInMsZodSchema,
    durationInNightsZodSchema,
    durationInSecondsZodSchema,
    inDays,
    inHours,
    inMilliseconds,
    inMinutes,
    inNights,
    inSeconds,
};
export type {
    Duration,
    DurationInDays,
    DurationInHours,
    DurationInMinutes,
    DurationInMs,
    DurationInNights,
    DurationInSeconds,
    TimeSpanInSeconds,
};
